import React from 'react';
import { CardSurface, Tabs } from '@spark';
import { useIsMobile } from '@/shared/hooks/useMediaQuery';
import './SideTabsLayout.css';

/**
 * Vertical side-tab rail on the left with the active tab's content on the
 * right (Figma Model Creation, frame 3001-71577). Shared by the task page's
 * Data Editor and Evaluations tabs.
 *
 * At or below `MOBILE_BREAKPOINT` the rail becomes a horizontally scrollable
 * top tab bar stacked above the content; the desktop markup is unchanged.
 *
 * @param {Object} props
 * @param {{id: string, label: React.ReactNode}[]} props.items
 * @param {string} props.activeId
 * @param {(id: string) => void} props.onChange
 * @param {boolean} [props.show=true] When false, renders children without the
 *   rail (empty states keep a full-width card layout).
 * @param {boolean} [props.withCard=false] Wrap children in a white CardSurface
 *   (Data Editor / Evaluations content pane).
 * @param {React.ReactNode} [props.railFooter] Shown under the tab list.
 * @param {React.ReactNode} props.children Content of the active tab.
 */
export default function SideTabsLayout({
    items,
    activeId,
    onChange,
    show = true,
    withCard = false,
    railFooter,
    children,
}) {
    const isMobile = useIsMobile();

    if (!show) return children ?? null;

    const content = withCard ? (
        <CardSurface background="white" bordered={false} padding="large">
            {children}
        </CardSurface>
    ) : (
        children
    );

    if (isMobile) {
        return (
            <div className="side-tabs-layout side-tabs-layout--mobile">
                <Tabs
                    className="side-tabs-layout__top-tabs"
                    items={items}
                    activeId={activeId}
                    onChange={onChange}
                    position="top"
                    scrollable
                />
                {railFooter ? (
                    <div className="side-tabs-layout__rail-footer">{railFooter}</div>
                ) : null}
                <div className="side-tabs-layout__content">{content}</div>
            </div>
        );
    }

    /* Vertical rail per Figma: position="right" places the divider on
       the list's left edge and the active indicator as a left border. */
    const rail = (
        <Tabs
            className="side-tabs-layout__rail"
            items={items}
            activeId={activeId}
            onChange={onChange}
            size="large"
            position="right"
        />
    );

    return (
        <div className="side-tabs-layout">
            {railFooter ? (
                <div className="side-tabs-layout__side">
                    {rail}
                    <div className="side-tabs-layout__rail-footer">{railFooter}</div>
                </div>
            ) : (
                rail
            )}
            <div className="side-tabs-layout__content">{content}</div>
        </div>
    );
}
